import React from "react";
import { AlertTriangle } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { cn } from "@siena-ai/design-system";
import type { ShareSnapshot } from "../types";
import { ReadOnlyContent } from "./ReadOnlyContent";
import { ThemeToggle } from "./ThemeToggle";
import { DocsTableOfContents } from "./docs-table-of-contents";
import { htmlToTocMarkdown, injectHeadingIds } from "./DocsSiteLayout";
import {
  transformHtmlEmbeds,
  attachHtmlEmbedFullscreen,
} from "./transform-html-embeds";

const HEADER_H = "3.5rem";

interface ShareViewProps {
  snapshot: ShareSnapshot;
}

function formatExpiredAt(expiresAt: string): string {
  try {
    return new Date(expiresAt).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return expiresAt;
  }
}

export const ShareView: React.FC<ShareViewProps> = ({ snapshot }) => {
  const { space, page, expiresAt } = snapshot;
  const isExpired = new Date(expiresAt) < new Date();

  const mainRef = React.useRef<HTMLDivElement | null>(null);

  // Rebuild the TOC from the rendered DOM so both htmlContent and
  // Tiptap-rendered content are covered. A MutationObserver keeps it in sync
  // with ReadOnlyContent, which mounts its editor asynchronously.
  const [tocContent, setTocContent] = React.useState(() =>
    htmlToTocMarkdown(page.htmlContent || undefined)
  );

  React.useEffect(() => {
    if (isExpired) return;
    const root = mainRef.current;
    if (!root) return;

    let raf = 0;
    const sync = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        injectHeadingIds(root);
        setTocContent(htmlToTocMarkdown(root.innerHTML));
      });
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [isExpired, page.htmlContent, page.tiptapContent]);

  React.useEffect(() => {
    if (isExpired || page.tiptapContent) return;
    return attachHtmlEmbedFullscreen();
  }, [isExpired, page.tiptapContent, page.htmlContent]);

  const showToc = !isExpired && Boolean(tocContent);

  return (
    <>
      <Helmet>
        <title>
          {page.title} - {space.name}
        </title>
        {space.branding.faviconUrl && (
          <>
            <link rel="icon" href={space.branding.faviconUrl} sizes="any" type={space.branding.faviconUrl.endsWith(".svg") ? "image/svg+xml" : "image/png"} />
            <link rel="icon" href={space.branding.faviconUrl} sizes="32x32" />
            <link rel="apple-touch-icon" sizes="180x180" href={space.branding.faviconUrl} />
          </>
        )}
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div
        className="min-h-screen bg-background"
        style={
          {
            "--brand-color": space.brandColor
              ? `#${space.brandColor.replace(/^#/, "")}`
              : undefined,
          } as React.CSSProperties
        }
      >
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80">
          <div className="mx-auto flex items-center h-14 px-6 max-w-[1280px]">
            <div className="flex items-center shrink-0">
              {space.branding.logoUrl ? (
                <img
                  src={space.branding.logoUrl}
                  alt={space.name}
                  width={121}
                  height={32}
                  className="max-w-[121px] max-h-8 object-contain dark:hidden"
                />
              ) : (
                <span className="text-sm font-semibold">{space.name}</span>
              )}
              {space.branding.logoDarkUrl && (
                <img
                  src={space.branding.logoDarkUrl}
                  alt={space.name}
                  width={121}
                  height={32}
                  className="max-w-[121px] max-h-8 object-contain hidden dark:block"
                />
              )}
            </div>
            <div className="flex-1 flex items-center justify-end gap-2">
              <ThemeToggle variant="ghost" size="icon-sm" />
            </div>
          </div>
        </header>

        {isExpired ? (
          <div className="mx-auto max-w-xl px-6 py-24 text-center">
            <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <h1 className="text-xl font-semibold text-foreground">
              This share link has expired
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              The link expired on {formatExpiredAt(expiresAt)}. Ask the person
              who shared it to generate a new one.
            </p>
          </div>
        ) : (
          <div
            className={cn(
              "mx-auto flex gap-8 px-6",
              showToc ? "max-w-[1280px]" : "max-w-3xl"
            )}
          >
            <main className={cn("flex-1 min-w-0", !showToc && "max-w-3xl")}>
              <div className="pt-3.5 pb-10 sm:pb-12 lg:pb-16">
                <header className="mb-8">
                  <h1 className="text-2xl font-bold text-foreground">
                    {page.title}
                  </h1>
                  {page.description && (
                    <p className="mt-2 text-base text-muted-foreground">
                      {page.description}
                    </p>
                  )}
                </header>

                <div ref={mainRef}>
                  {page.tiptapContent ? (
                    <ReadOnlyContent content={page.tiptapContent} />
                  ) : (
                    <div
                      className="help-article-content prose prose-sm max-w-none text-[15px] leading-relaxed dark:prose-invert"
                      dangerouslySetInnerHTML={{ __html: transformHtmlEmbeds(page.htmlContent) }}
                    />
                  )}
                </div>
              </div>
            </main>

            {showToc && (
              <DocsTableOfContents
                content={tocContent}
                className="hidden xl:block pt-5 pb-10"
                stickyOffset={HEADER_H}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};
